'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useTransition, useEffect, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import {
  Home, MapPin, FileText, Package, CheckCircle2,
  ChevronLeft, ChevronRight, Loader2,
  Star, BedDouble, Maximize2,
  BadgeCheck, Lock, User, Camera, Upload, X, File,
  AlertCircle, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/AuthContext';
import { publicApi, listingApi } from '@/lib/api';
import { cn, getPropertyTypeLabel, PROPERTY_TYPE_GROUPS } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormData {
  transactionType: 'sale' | 'rent';
  type: string;
  regionId: string;
  regionName: string;
  cityId: string;
  cityName: string;
  districtId: string;
  districtName: string;
  address: string;
  title: string;
  description: string;
  price: string;
  surface: string;
  rooms: string;
  bedrooms: string;
  bathrooms: string;
  floor: string;
  yearBuilt: string;
  condition: string;
  furnished: boolean;
  packId: string;
}

interface Pack {
  id: string;
  name: string;
  price: number;
  durationDays: number;
  maxPhotos: number;
  isFeatured: boolean;
  boostLevel: number;
  features: string[];
  isActive: boolean;
}

interface City { id: string; name: string; }
interface Region { id: string; name: string; }
interface District { id: string; name: string; }
interface PhotoItem { file: File; preview: string; id: string; }
interface DocItem { file: File; label: string; id: string; }

// ─── Constants ────────────────────────────────────────────────────────────────


const CONDITIONS = [
  { value: 'new', label: 'Neuf' },
  { value: 'good', label: 'Bon état' },
  { value: 'renovated', label: 'Rénové' },
  { value: 'to_renovate', label: 'À rénover' },
];

const STEPS = [
  { id: 1, label: 'Type de bien', icon: Home },
  { id: 2, label: 'Localisation', icon: MapPin },
  { id: 3, label: 'Détails', icon: FileText },
  { id: 4, label: 'Photos', icon: Camera },
  { id: 5, label: 'Documents', icon: File },
  { id: 6, label: 'Pack', icon: Package },
  { id: 7, label: 'Confirmation', icon: CheckCircle2 },
];

const API_BASE =
  typeof window === 'undefined'
    ? process.env.API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000'
    : process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

// ─── Upload helpers ───────────────────────────────────────────────────────────

async function uploadFiles(files: File[], propertyId: string, token: string) {
  if (files.length === 0) return;
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  fd.append('propertyId', propertyId);
  const res = await fetch(`${API_BASE}/api/media/upload-multiple`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error('Échec de l\'envoi des photos.');
}

async function uploadDoc(file: File, propertyId: string, token: string) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('propertyId', propertyId);
  const res = await fetch(`${API_BASE}/api/media/upload-document`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error('Échec de l\'envoi d\'un document.');
}

// ─── Required documents helper ───────────────────────────────────────────────

function getRequiredDocs(
  type: string,
  transactionType: 'sale' | 'rent',
  ownerType?: string | null,
): { key: string; label: string }[] {
  if (ownerType === 'agence') {
    return [{ key: 'cin', label: 'Copie CIN' }];
  }
  if (transactionType === 'rent') {
    return [
      { key: 'cin', label: 'CIN' },
      { key: 'titre_propriete', label: 'Acte de propriété' },
    ];
  }
  const docs: { key: string; label: string }[] = [
    { key: 'cin', label: 'CIN' },
    { key: 'titre_propriete', label: 'Acte de propriété' },
    { key: 'acte_acquisition', label: "Acte d'acquisition" },
    { key: 'autorisation_construire', label: 'Autorisation de construire' },
    { key: 'permidabilite', label: 'Pérmidabité' },
    { key: 'croquis', label: 'Le Croquis (Plan Cadastral)' },
  ];
  if (type === 'terrain_urbain') {
    docs.push({ key: 'note_agence_urbaine', label: "La note de renseignements (l'agence urbaine)" });
  }
  return docs;
}

// ─── Main page ────────────────────────────────────────────────────────────────

function PublierPageContent() {
  const searchParams = useSearchParams();
  const { user, accessToken, loading: authLoading } = useAuth();
  const router = useRouter();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submittedSlug, setSubmittedSlug] = useState('');
  const [isPending, startTransition] = useTransition();
  const [uploadProgress, setUploadProgress] = useState('');
  const [packs, setPacks] = useState<Pack[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionSearch, setRegionSearch] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [citySearch, setCitySearch] = useState('');
  const [districts, setDistricts] = useState<District[]>([]);
  const [districtSearch, setDistrictSearch] = useState('');
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [docDragOver, setDocDragOver] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const requiredDocInputRef = useRef<HTMLInputElement>(null);
  const previewUrlsRef = useRef<string[]>([]);
  const [requiredDocFiles, setRequiredDocFiles] = useState<Record<string, File>>({});
  const [pendingRequiredDocKey, setPendingRequiredDocKey] = useState<string | null>(null);

  const preselectedPackId = searchParams.get('pack') ?? '';
  const ownerType = searchParams.get('ownerType');

  const [form, setForm] = useState<FormData>({
    transactionType: 'sale',
    type: '',
    regionId: '',
    regionName: '',
    cityId: '',
    cityName: '',
    districtId: '',
    districtName: '',
    address: '',
    title: '',
    description: '',
    price: '',
    surface: '',
    rooms: '',
    bedrooms: '',
    bathrooms: '',
    floor: '',
    yearBuilt: '',
    condition: 'good',
    furnished: false,
    packId: preselectedPackId,
  });

  const selectedPack = packs.find((p) => p.id === form.packId);
  const maxPhotos = selectedPack?.maxPhotos ?? 10;

  // Load packs + regions on mount
  useEffect(() => {
    publicApi.getPacks().then((data) => {
      setPacks(((data as Pack[]) || []).filter((p) => p.isActive));
      if (preselectedPackId) setForm((f) => ({ ...f, packId: preselectedPackId }));
    }).catch(() => { });
    publicApi.getRegions().then((data) => {
      setRegions((data as Region[]) || []);
    }).catch(() => { });
  }, [preselectedPackId]);

  // Load cities when region changes
  useEffect(() => {
    if (!form.regionId) { setCities([]); return; }
    publicApi.getCities(form.regionId).then((data) => {
      setCities((data as City[]) || []);
    }).catch(() => { });
  }, [form.regionId]);

  // Load districts when city changes
  useEffect(() => {
    if (!form.cityId) { setDistricts([]); return; }
    publicApi.getDistricts(form.cityId).then((data) => {
      setDistricts((data as District[]) || []);
    }).catch(() => { });
  }, [form.cityId]);

  // Revoke object URLs on unmount
  useEffect(() => () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  // ─── Photo handlers ──────────────────────────────────────────────────────

  function addPhotos(files: FileList | null) {
    if (!files) return;
    const incoming: PhotoItem[] = [];
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      if (photos.length + incoming.length >= maxPhotos) return;
      const preview = URL.createObjectURL(file);
      previewUrlsRef.current.push(preview);
      incoming.push({ file, preview, id: `${Date.now()}-${Math.random()}` });
    });
    setPhotos((prev) => [...prev, ...incoming]);
  }

  function removePhoto(id: string) {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  }

  // ─── Document handlers ──────────────────────────────────────────────────

  function addDocs(files: FileList | null) {
    if (!files) return;
    const ALLOWED_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
    const incoming: DocItem[] = Array.from(files)
      .filter((f) => ALLOWED_EXTS.includes(f.name.split('.').pop()?.toLowerCase() ?? ''))
      .map((file) => ({
        file,
        label: file.name.replace(/\.[^/.]+$/, ''),
        id: `${Date.now()}-${Math.random()}`,
      }));
    setDocs((prev) => [...prev, ...incoming]);
  }

  function removeDoc(id: string) {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  }

  function updateDocLabel(id: string, label: string) {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, label } : d)));
  }

  // ─── Validation ──────────────────────────────────────────────────────────

  function validate(s: number): string {
    if (s === 1 && !form.type) return 'Sélectionnez un type de bien.';
    if (s === 2) {
      if (!form.regionId) return 'Sélectionnez une région.';
      if (!form.cityId) return 'Sélectionnez une ville.';
      if (!form.districtId) return 'Sélectionnez un quartier.';
      if (!form.address.trim()) return "Indiquez l'adresse du bien.";
    }
    if (s === 3) {
      if (!form.title.trim()) return 'Indiquez un titre pour votre annonce.';
      if (!form.description.trim()) return 'Ajoutez une description.';
      if (!form.price || isNaN(Number(form.price)) || Number(form.price) <= 0)
        return 'Indiquez un prix valide.';
    }
    if (s === 5) {
      const required = getRequiredDocs(form.type, form.transactionType, ownerType);
      const missing = required.filter((d) => !requiredDocFiles[d.key]);
      if (missing.length > 0)
        return `Documents manquants : ${missing.map((d) => d.label).join(', ')}.`;
    }
    return '';
  }

  function next() {
    const err = validate(step);
    if (err) { setError(err); return; }
    setError('');
    setStep((s) => s + 1);
  }

  function back() {
    setError('');
    setStep((s) => s - 1);
  }

  // ─── Submit ──────────────────────────────────────────────────────────────

  async function submit() {
    if (!user || !accessToken) {
      router.push(`/auth/login?redirect=${encodeURIComponent('/publier')}`);
      return;
    }
    setError('');
    startTransition(async () => {
      try {
        setUploadProgress("Création de l'annonce…");
        const created = await listingApi.create(
          {
            title: form.title.trim(),
            description: form.description.trim(),
            price: Number(form.price),
            type: form.type,
            transactionType: form.transactionType,
            surface: form.surface ? Number(form.surface) : undefined,
            rooms: form.rooms ? Number(form.rooms) : undefined,
            bedrooms: form.bedrooms ? Number(form.bedrooms) : undefined,
            bathrooms: form.bathrooms ? Number(form.bathrooms) : undefined,
            floor: form.floor ? Number(form.floor) : undefined,
            yearBuilt: form.yearBuilt ? Number(form.yearBuilt) : undefined,
            condition: form.condition,
            furnished: form.furnished,
            address: form.address.trim() || undefined,
            cityId: form.cityId || undefined,
            regionId: form.regionId || undefined,
            districtId: form.districtId || undefined,
            status: 'pending',
            packId: form.packId || undefined,
          },
          accessToken,
        );
        const propertyId = (created as any)?.id as string | undefined;

        if (photos.length > 0 && propertyId) {
          setUploadProgress(`Envoi des photos (${photos.length})…`);
          await uploadFiles(photos.map((p) => p.file), propertyId, accessToken);
        }

        const allDocFiles = [
          ...getRequiredDocs(form.type, form.transactionType, ownerType)
            .filter((d) => requiredDocFiles[d.key])
            .map((d) => requiredDocFiles[d.key] as File),
          ...docs.map((d) => d.file),
        ];
        if (allDocFiles.length > 0 && propertyId) {
          for (let i = 0; i < allDocFiles.length; i++) {
            setUploadProgress(`Envoi des documents (${i + 1}/${allDocFiles.length})…`);
            await uploadDoc(allDocFiles[i], propertyId, accessToken);
          }
        }

        setSubmittedSlug((created as any)?.slug ?? '');
        setDone(true);
      } catch (e: any) {
        setError(e?.message ?? 'Une erreur est survenue. Veuillez réessayer.');
        setUploadProgress('');
      }
    });
  }

  // ─── Auth gate ────────────────────────────────────────────────────────────

  if (authLoading) {
    return (
      <div className="min-h-screen bg-surface-cream flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-surface-cream flex items-center justify-center p-4">
        <div className="bg-surface-light rounded-2xl border border-border-subtle shadow-card-hover p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Publiez votre annonce</h1>
          <p className="text-muted-foreground text-sm leading-relaxed mb-8">
            Connectez-vous ou créez un compte gratuitement pour déposer votre annonce en quelques minutes.
          </p>
          <div className="space-y-3">
            <Link href={`/auth/login?from=${encodeURIComponent('/publier')}`}>
              <Button variant="navy" className="w-full" size="lg">
                <User className="h-4 w-4 mr-2" />
                Se connecter
              </Button>
            </Link>
            <Link href={`/auth/register?from=${encodeURIComponent('/publier')}`}>
              <Button variant="outline" className="w-full" size="lg">
                Créer un compte
              </Button>
            </Link>
          </div>
          <p className="text-xs text-muted-foreground mt-6">
            Inscription gratuite &bull; Aucune carte bancaire requise
          </p>
        </div>
      </div>
    );
  }

  // ─── Success screen ────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-gray-50 flex items-center justify-center p-4">
        <div className="bg-surface-light rounded-2xl border border-border-subtle shadow-card-hover p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6 ring-8 ring-success/10">
            <BadgeCheck className="h-10 w-10 text-success" />
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Annonce déposée !</h1>
          <p className="text-muted-foreground leading-relaxed mb-2">
            Votre annonce est en cours de validation par notre équipe.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Elle sera publiée sous 24 h ouvrées. Vous recevrez un e-mail de confirmation.
          </p>
          <div className="space-y-3">
            {submittedSlug && (
              <Link href={`/annonce/${submittedSlug}`}>
                <Button variant="navy" className="w-full">Voir mon annonce</Button>
              </Link>
            )}
            <Link href="/">
              <Button variant="outline" className="w-full">Retour à l'accueil</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const filteredRegions = regionSearch.trim()
    ? regions.filter((r) => r.name.toLowerCase().includes(regionSearch.toLowerCase()))
    : regions;
  const filteredCities = citySearch.trim()
    ? cities.filter((c) => c.name.toLowerCase().includes(citySearch.toLowerCase()))
    : cities;
  const filteredDistricts = districtSearch.trim()
    ? districts.filter((d) => d.name.toLowerCase().includes(districtSearch.toLowerCase()))
    : districts;

  const progress = Math.round(((step - 1) / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-surface-cream">
      {/* ── Sticky top bar ────────────────────────────────────────────────── */}
      <div className="bg-surface-light border-b sticky top-0 z-30 shadow-sm">
        <div className="container flex items-center justify-between py-3.5">
          <div>
            <h1 className="text-base font-bold text-ink leading-tight">Publier une annonce</h1>
            <p className="text-xs text-muted-foreground">
              {STEPS[step - 1]?.label} · étape {step}/{STEPS.length}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-primary">{progress}%</span>
            <div className="w-28 h-1.5 rounded-full bg-surface-cream overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8 max-w-5xl mx-auto px-4">
        <div className="flex gap-8 items-start">

          {/* ── Desktop sidebar ──────────────────────────────────────────── */}
          <aside className="hidden lg:block w-52 shrink-0 sticky top-[72px]">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 px-1">Étapes</p>
            <nav className="space-y-0.5">
              {STEPS.map((s) => {
                const isDone = step > s.id;
                const isActive = step === s.id;
                return (
                  <div
                    key={s.id}
                    className={cn(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all',
                      isActive ? 'bg-primary/10' : isDone ? 'bg-success/5' : '',
                    )}
                  >
                    <div className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all',
                      isActive
                        ? 'bg-primary text-white shadow-md shadow-primary/20'
                        : isDone
                          ? 'bg-success/50 text-white'
                          : 'bg-surface-cream text-ink-muted',
                    )}>
                      {isDone ? <Check className="h-3.5 w-3.5" /> : s.id}
                    </div>
                    <span className={cn(
                      'text-sm font-medium transition-colors',
                      isActive ? 'text-primary' : isDone ? 'text-success' : 'text-ink-muted',
                    )}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </nav>
          </aside>

          {/* ── Form card ────────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {/* Mobile step pills */}
            <div className="flex gap-1.5 mb-5 lg:hidden overflow-x-auto pb-1">
              {STEPS.map((s) => {
                const isDone = step > s.id;
                const isActive = step === s.id;
                return (
                  <div
                    key={s.id}
                    className={cn(
                      'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium shrink-0 transition-all',
                      isActive ? 'bg-primary text-white' : isDone ? 'bg-success/10 text-success' : 'bg-surface-cream text-ink-muted',
                    )}
                  >
                    {isDone ? <Check className="h-3 w-3" /> : <span>{s.id}</span>}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="bg-surface-light rounded-2xl border border-border-subtle shadow-sm overflow-hidden">
              <div className="p-6 sm:p-8">

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 1 — Type & Transaction                             */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 1 && (
                  <div className="space-y-7">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Quel bien souhaitez-vous publier ?</h2>
                      <p className="text-sm text-muted-foreground">Sélectionnez le type de transaction et de propriété</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-ink mb-3">Type de transaction</label>
                      <div className="grid grid-cols-2 gap-3 max-w-sm">
                        {(['sale', 'rent'] as const).map((tx) => (
                          <button
                            key={tx}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, transactionType: tx }))}
                            className={cn(
                              'flex flex-col items-center gap-2.5 rounded-2xl border-2 p-5 text-sm font-semibold transition-all',
                              form.transactionType === tx
                                ? 'border-primary bg-primary/5 text-primary shadow-sm'
                                : 'border-border-subtle text-ink-secondary hover:border-border hover:bg-surface-cream',
                            )}
                          >
                            <span className="text-3xl">{tx === 'sale' ? '🏷️' : '🔑'}</span>
                            {tx === 'sale' ? 'Vente' : 'Location'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-ink mb-3">Type de bien</label>
                      <div className="space-y-4">
                        {PROPERTY_TYPE_GROUPS.filter((g) =>
                          form.transactionType === 'sale' || g.available_for.includes('rent')
                        ).map((group) => (
                          <div key={group.group_key}>
                            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-2">
                              {group.group_label}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {group.values.map(({ key, label }) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setForm((f) => ({ ...f, type: key }))}
                                  className={cn(
                                    'flex flex-col items-center gap-2 rounded-2xl border-2 p-4 text-sm font-medium transition-all',
                                    form.type === key
                                      ? 'border-primary bg-primary/5 text-primary shadow-sm'
                                      : 'border-border-subtle text-ink-secondary hover:border-border hover:bg-surface-cream',
                                  )}
                                >
                                  <span className="text-xs">{label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 2 — Localisation                                   */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Où est situé le bien ?</h2>
                      <p className="text-sm text-muted-foreground">Indiquez la région, la ville, le quartier et l'adresse du bien</p>
                    </div>

                    {/* Région */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-ink mb-1.5">
                        Région <span className="text-error">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-10"
                          placeholder="Rechercher une région…"
                          value={regionSearch || form.regionName}
                          onChange={(e) => {
                            setRegionSearch(e.target.value);
                            if (form.regionId) setForm((f) => ({ ...f, regionId: '', regionName: '', cityId: '', cityName: '', districtId: '', districtName: '' }));
                          }}
                        />
                      </div>
                      {regionSearch && filteredRegions.length > 0 && (
                        <div className="absolute left-0 right-0 border rounded-xl mt-1 bg-surface-light shadow-xl z-10 max-h-52 overflow-y-auto">
                          {filteredRegions.slice(0, 10).map((region) => (
                            <button
                              key={region.id}
                              type="button"
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-cream flex items-center gap-2 first:rounded-t-xl last:rounded-b-xl"
                              onClick={() => {
                                setForm((f) => ({ ...f, regionId: region.id, regionName: region.name, cityId: '', cityName: '', districtId: '', districtName: '' }));
                                setRegionSearch('');
                                setCitySearch('');
                                setDistrictSearch('');
                              }}
                            >
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              {region.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {form.regionName && !regionSearch && (
                        <p className="text-xs text-success mt-1.5 flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {form.regionName} sélectionné
                        </p>
                      )}
                    </div>

                    {/* Ville */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-ink mb-1.5">
                        Ville <span className="text-error">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-10"
                          placeholder={form.regionId ? 'Rechercher une ville…' : 'Sélectionnez d\'abord une région'}
                          disabled={!form.regionId}
                          value={citySearch || form.cityName}
                          onChange={(e) => {
                            setCitySearch(e.target.value);
                            if (form.cityId) setForm((f) => ({ ...f, cityId: '', cityName: '', districtId: '', districtName: '' }));
                          }}
                        />
                      </div>
                      {citySearch && filteredCities.length > 0 && (
                        <div className="absolute left-0 right-0 border rounded-xl mt-1 bg-surface-light shadow-xl z-10 max-h-52 overflow-y-auto">
                          {filteredCities.slice(0, 10).map((city) => (
                            <button
                              key={city.id}
                              type="button"
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-cream flex items-center gap-2 first:rounded-t-xl last:rounded-b-xl"
                              onClick={() => {
                                setForm((f) => ({ ...f, cityId: city.id, cityName: city.name, districtId: '', districtName: '' }));
                                setCitySearch('');
                                setDistrictSearch('');
                              }}
                            >
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              {city.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {form.cityName && !citySearch && (
                        <p className="text-xs text-success mt-1.5 flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {form.cityName} sélectionné
                        </p>
                      )}
                    </div>

                    {/* Quartier */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-ink mb-1.5">
                        Quartier <span className="text-error">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-10"
                          placeholder={form.cityId ? 'Rechercher un quartier…' : 'Sélectionnez d\'abord une ville'}
                          disabled={!form.cityId}
                          value={districtSearch || form.districtName}
                          onChange={(e) => {
                            setDistrictSearch(e.target.value);
                            if (form.districtId) setForm((f) => ({ ...f, districtId: '', districtName: '' }));
                          }}
                        />
                      </div>
                      {districtSearch && filteredDistricts.length > 0 && (
                        <div className="absolute left-0 right-0 border rounded-xl mt-1 bg-surface-light shadow-xl z-10 max-h-52 overflow-y-auto">
                          {filteredDistricts.slice(0, 15).map((district) => (
                            <button
                              key={district.id}
                              type="button"
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-cream flex items-center gap-2 first:rounded-t-xl last:rounded-b-xl"
                              onClick={() => {
                                setForm((f) => ({ ...f, districtId: district.id, districtName: district.name }));
                                setDistrictSearch('');
                              }}
                            >
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              {district.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {form.districtName && !districtSearch && (
                        <p className="text-xs text-success mt-1.5 flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {form.districtName} sélectionné
                        </p>
                      )}
                    </div>

                    {/* Adresse */}
                    <div>
                      <label className="block text-sm font-semibold text-ink mb-1.5">
                        Adresse <span className="text-error">*</span>
                      </label>
                      <Input
                        placeholder="Ex : 12 Rue de la Palmeraie"
                        value={form.address}
                        required
                        onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                      />
                    </div>
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 3 — Détails                                        */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 3 && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Décrivez votre bien</h2>
                      <p className="text-sm text-muted-foreground">Ces informations seront affichées dans votre annonce</p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">
                          Titre de l'annonce <span className="text-error">*</span>
                        </label>
                        <Input
                          placeholder="Ex : Belle villa avec piscine à la Palmeraie, Marrakech"
                          value={form.title}
                          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                          maxLength={150}
                        />
                        <p className="text-xs text-muted-foreground mt-1 text-right">{form.title.length}/150</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">
                          Description <span className="text-error">*</span>
                        </label>
                        <textarea
                          rows={5}
                          placeholder="Décrivez votre bien : caractéristiques, équipements, environnement, points forts…"
                          value={form.description}
                          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                          className="w-full rounded-xl border border-border-subtle px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition-shadow"
                          maxLength={3000}
                        />
                        <p className="text-xs text-muted-foreground mt-1 text-right">{form.description.length}/3000</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">
                          Prix demandé {form.transactionType === 'rent' ? '(MAD/mois)' : '(MAD)'} <span className="text-error">*</span>
                        </label>
                        <div className="relative">
                          <Input
                            type="number"
                            min={0}
                            className="pr-20"
                            placeholder={form.transactionType === 'rent' ? 'Ex : 8 000' : 'Ex : 1 500 000'}
                            value={form.price}
                            onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                          />
                          <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium pointer-events-none">
                            {form.transactionType === 'rent' ? 'MAD/m' : 'MAD'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-ink mb-3">Caractéristiques</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                            <Maximize2 className="h-3.5 w-3.5" /> Surface (m²)
                          </label>
                          <Input type="number" min={0} placeholder="Ex : 200" value={form.surface}
                            onChange={(e) => setForm((f) => ({ ...f, surface: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5">Pièces</label>
                          <Input type="number" min={0} max={30} placeholder="Ex : 5" value={form.rooms}
                            onChange={(e) => setForm((f) => ({ ...f, rooms: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                            <BedDouble className="h-3.5 w-3.5" /> Chambres
                          </label>
                          <Input type="number" min={0} max={20} placeholder="Ex : 3" value={form.bedrooms}
                            onChange={(e) => setForm((f) => ({ ...f, bedrooms: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5">Salles de bain</label>
                          <Input type="number" min={0} max={10} placeholder="Ex : 2" value={form.bathrooms}
                            onChange={(e) => setForm((f) => ({ ...f, bathrooms: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5">Étage</label>
                          <Input type="number" min={0} placeholder="Ex : 3" value={form.floor}
                            onChange={(e) => setForm((f) => ({ ...f, floor: e.target.value }))} />
                        </div>
                        <div>
                          <label className="block text-xs text-muted-foreground mb-1.5">Année de construction</label>
                          <Input type="number" min={1900} max={2030} placeholder="Ex : 2020" value={form.yearBuilt}
                            onChange={(e) => setForm((f) => ({ ...f, yearBuilt: e.target.value }))} />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-end">
                      <div>
                        <label className="block text-sm font-semibold text-ink mb-1.5">État du bien</label>
                        <select
                          value={form.condition}
                          onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
                          className="w-full rounded-xl border border-border-subtle bg-surface-light px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        >
                          {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                    </div>

                    <div
                      className={cn(
                        'flex items-center justify-between w-full rounded-xl border px-4 py-3.5 cursor-pointer select-none transition-all',
                        form.furnished
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border-subtle bg-surface-light hover:bg-surface-cream',
                      )}
                      onClick={() => setForm((f) => ({ ...f, furnished: !f.furnished }))}
                    >
                      <div>
                        <p className="text-sm font-medium text-ink">Bien meublé</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Les meubles sont inclus dans le prix</p>
                      </div>
                      {/* Switch track */}
                      <div
                        role="switch"
                        aria-checked={form.furnished}
                        className={cn(
                          'relative inline-flex h-6 w-11 items-center rounded-full border-2 transition-colors duration-200 ease-in-out shrink-0',
                          form.furnished ? 'bg-primary border-primary' : 'bg-border border-border-subtle',
                        )}
                      >
                        {/* Switch thumb */}
                        <span
                          className={cn(
                            'inline-block h-4 w-4 rounded-full bg-surface-light shadow-md ring-0 transition-transform duration-200 ease-in-out',
                            form.furnished ? 'translate-x-5' : 'translate-x-1',
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 4 — Photos                                          */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 4 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Photos du bien</h2>
                      <p className="text-sm text-muted-foreground">
                        Jusqu'à {maxPhotos} photos · JPG, PNG, WebP · 10 Mo max par fichier
                      </p>
                    </div>

                    {/* Drop zone */}
                    {/* Hidden inputs — outside any clickable area to prevent event bubbling */}
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
                    />

                    {photos.length < maxPhotos && (
                      <div
                        onDragOver={(e) => { e.preventDefault(); setPhotoDragOver(true); }}
                        onDragLeave={() => setPhotoDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setPhotoDragOver(false); addPhotos(e.dataTransfer.files); }}
                        onClick={() => photoInputRef.current?.click()}
                        className={cn(
                          'border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all',
                          photoDragOver
                            ? 'border-primary bg-primary/5 scale-[1.01]'
                            : 'border-border-subtle hover:border-primary/50 hover:bg-surface-cream/80',
                        )}
                      >
                        <div className="flex flex-col items-center gap-3 pointer-events-none">
                          <div className={cn(
                            'w-16 h-16 rounded-2xl flex items-center justify-center transition-all',
                            photoDragOver ? 'bg-primary/15' : 'bg-surface-cream',
                          )}>
                            <Camera className={cn('h-8 w-8 transition-colors', photoDragOver ? 'text-primary' : 'text-ink-muted')} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-ink">
                              {photoDragOver ? 'Relâchez pour ajouter' : 'Glissez vos photos ici'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">ou cliquez pour parcourir</p>
                          </div>
                          <Badge variant="outline" className="text-xs">{photos.length}/{maxPhotos} photos</Badge>
                        </div>
                      </div>
                    )}

                    {/* Preview grid */}
                    {photos.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
                          <Star className="h-3 w-3 text-accent" />
                          La première photo est utilisée comme photo principale
                        </p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                          {photos.map((photo, idx) => (
                            <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden bg-surface-cream shadow-sm">
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                              {idx === 0 && (
                                <div className="absolute top-1.5 left-1.5">
                                  <Badge className="text-[10px] px-1.5 py-0.5 bg-primary shadow-sm">⭐ Principale</Badge>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removePhoto(photo.id)}
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-primary/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error/100"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                          {photos.length < maxPhotos && (
                            <button
                              type="button"
                              onClick={() => photoInputRef.current?.click()}
                              className="aspect-square rounded-xl border-2 border-dashed border-border-subtle flex flex-col items-center justify-center gap-1 hover:border-primary/50 hover:bg-surface-cream transition-all text-muted-foreground"
                            >
                              <Upload className="h-5 w-5" />
                              <span className="text-xs">Ajouter</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {photos.length === 0 && (
                      <p className="text-center text-xs text-muted-foreground py-2 bg-blue-50 rounded-xl border border-blue-100 px-4">
                        💡 Les annonces avec photos reçoivent 5× plus de visites. Vous pouvez aussi en ajouter après la publication.
                      </p>
                    )}
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 5 — Documents                                       */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 5 && (() => {
                  const requiredDocs = getRequiredDocs(form.type, form.transactionType, ownerType);
                  return (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-bold text-ink mb-1">Documents de la propriété</h2>
                        <p className="text-sm text-muted-foreground">
                          Fournissez les documents requis pour valider votre annonce
                        </p>
                      </div>

                      {/* Hidden input for required docs */}
                      <input
                        ref={requiredDocInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && pendingRequiredDocKey) {
                            setRequiredDocFiles((prev) => ({ ...prev, [pendingRequiredDocKey]: file }));
                            setPendingRequiredDocKey(null);
                          }
                          e.target.value = '';
                        }}
                      />

                      {/* Required documents */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <AlertCircle className="h-4 w-4 text-error shrink-0" />
                          <p className="text-sm font-semibold text-ink">Documents obligatoires</p>
                        </div>
                        <div className="space-y-2">
                          {requiredDocs.map((doc) => {
                            const file = requiredDocFiles[doc.key];
                            const sizeMB = file ? (file.size / 1024 / 1024).toFixed(1) : null;
                            return (
                              <div
                                key={doc.key}
                                className={cn(
                                  'flex items-center gap-3 rounded-xl border p-3.5 transition-all',
                                  file ? 'border-success/40 bg-success/5' : 'border-error/30 bg-error/5',
                                )}
                              >
                                <div className={cn(
                                  'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                                  file ? 'bg-success/15' : 'bg-error/10',
                                )}>
                                  {file
                                    ? <CheckCircle2 className="h-4 w-4 text-success" />
                                    : <File className="h-4 w-4 text-error" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={cn(
                                    'text-sm font-medium leading-tight',
                                    file ? 'text-ink' : 'text-ink',
                                  )}>{doc.label}</p>
                                  {file ? (
                                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{file.name} · {sizeMB} Mo</p>
                                  ) : (
                                    <p className="text-xs text-error mt-0.5">Requis</p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPendingRequiredDocKey(doc.key);
                                    requiredDocInputRef.current?.click();
                                  }}
                                  className={cn(
                                    'text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all shrink-0',
                                    file
                                      ? 'border-success/30 text-success hover:bg-success/10'
                                      : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10',
                                  )}
                                >
                                  {file ? 'Remplacer' : 'Télécharger'}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Optional additional documents */}
                      <div>
                        <p className="text-sm font-semibold text-ink mb-3">
                          Documents supplémentaires{' '}
                          <span className="font-normal text-muted-foreground">(optionnel)</span>
                        </p>

                        {/* Hidden input for optional docs */}
                        <input
                          ref={docInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          multiple
                          className="hidden"
                          onChange={(e) => { addDocs(e.target.files); e.target.value = ''; }}
                        />

                        {/* Drop zone */}
                        <div
                          onDragOver={(e) => { e.preventDefault(); setDocDragOver(true); }}
                          onDragLeave={() => setDocDragOver(false)}
                          onDrop={(e) => { e.preventDefault(); setDocDragOver(false); addDocs(e.dataTransfer.files); }}
                          onClick={() => docInputRef.current?.click()}
                          className={cn(
                            'border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all',
                            docDragOver
                              ? 'border-primary bg-primary/5 scale-[1.01]'
                              : 'border-border-subtle hover:border-primary/50 hover:bg-surface-cream/80',
                          )}
                        >
                          <div className="flex flex-col items-center gap-2 pointer-events-none">
                            <div className={cn(
                              'w-12 h-12 rounded-2xl flex items-center justify-center transition-all',
                              docDragOver ? 'bg-primary/15' : 'bg-surface-cream',
                            )}>
                              <File className={cn('h-6 w-6 transition-colors', docDragOver ? 'text-primary' : 'text-ink-muted')} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-ink">
                                {docDragOver ? 'Relâchez pour ajouter' : 'Glissez vos documents ici'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">PDF, DOC, DOCX, XLS</p>
                            </div>
                          </div>
                        </div>

                        {/* Optional doc list */}
                        {docs.length > 0 && (
                          <div className="space-y-2 mt-3">
                            {docs.map((doc) => {
                              const ext = doc.file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
                              const sizeMB = (doc.file.size / 1024 / 1024).toFixed(1);
                              return (
                                <div key={doc.id} className="flex items-center gap-3 rounded-xl border border-border-subtle bg-surface-cream p-3.5">
                                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <File className="h-5 w-5 text-primary" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <input
                                      className="w-full bg-transparent text-sm font-medium text-ink focus:outline-none border-b border-transparent hover:border-border-subtle focus:border-primary transition-colors pb-0.5"
                                      value={doc.label}
                                      onChange={(e) => updateDocLabel(doc.id, e.target.value)}
                                      placeholder="Nom du document"
                                    />
                                    <p className="text-xs text-muted-foreground mt-0.5">{ext} · {sizeMB} Mo</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeDoc(doc.id)}
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-error/10 hover:text-error transition-colors shrink-0"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 6 — Pack                                            */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 6 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Choisissez votre pack de visibilité</h2>
                      <p className="text-sm text-muted-foreground">
                        Le pack détermine la durée et la mise en avant de votre annonce
                      </p>
                    </div>

                    {packs.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground text-sm">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Chargement des packs…
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {packs.map((pack) => {
                          const isSelected = form.packId === pack.id;
                          return (
                            <button
                              key={pack.id}
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, packId: pack.id }))}
                              className={cn(
                                'w-full text-left rounded-2xl border-2 p-5 transition-all',
                                isSelected
                                  ? 'border-primary bg-primary/5 shadow-sm'
                                  : 'border-border-subtle hover:border-border-subtle hover:bg-surface-cream',
                              )}
                            >
                              <div className="flex items-start gap-4 justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-ink text-base">{pack.name}</span>
                                    {pack.isFeatured && <Badge variant="accent" className="text-xs">⭐ Populaire</Badge>}
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
                                    <span>📅 {pack.durationDays} jours</span>
                                    <span>📷 {pack.maxPhotos} photos max</span>
                                    {pack.isFeatured && <span className="text-success font-medium">🚀 Mise en avant</span>}
                                  </div>
                                  {pack.features?.length > 0 && (
                                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                                      {pack.features.slice(0, 4).map((feat) => (
                                        <span key={feat} className="text-xs text-ink-secondary flex items-center gap-1">
                                          <Check className="h-3 w-3 text-success shrink-0" />
                                          {feat}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right shrink-0 flex flex-col items-end gap-2">
                                  <div>
                                    <div className="text-2xl font-bold text-ink leading-none">
                                      {pack.price === 0 ? 'Gratuit' : pack.price}
                                    </div>
                                    {pack.price > 0 && <div className="text-xs text-muted-foreground">MAD</div>}
                                  </div>
                                  <div className={cn(
                                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                                    isSelected ? 'border-primary bg-primary' : 'border-border',
                                  )}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ──────────────────────────────────────────────────────── */}
                {/* Step 7 — Confirmation                                    */}
                {/* ──────────────────────────────────────────────────────── */}
                {step === 7 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold text-ink mb-1">Récapitulatif</h2>
                      <p className="text-sm text-muted-foreground">Vérifiez toutes les informations avant de publier</p>
                    </div>

                    <div className="rounded-2xl border border-border-subtle overflow-hidden divide-y divide-border-subtle">
                      {([
                        { label: 'Type', value: `${getPropertyTypeLabel(form.type)} · ${form.transactionType === 'sale' ? 'Vente' : 'Location'}` },
                        { label: 'Ville', value: form.cityName + (form.address ? ` — ${form.address}` : '') },
                        { label: 'Titre', value: form.title },
                        { label: 'Prix demandé', value: `${Number(form.price).toLocaleString('fr-MA')} MAD${form.transactionType === 'rent' ? '/mois' : ''}` },
                        form.surface ? { label: 'Surface', value: `${form.surface} m²` } : null,
                        form.bedrooms ? { label: 'Chambres', value: form.bedrooms } : null,
                        photos.length > 0 ? { label: 'Photos', value: `${photos.length} photo${photos.length > 1 ? 's' : ''}` } : null,
                        docs.length > 0 ? { label: 'Documents', value: `${docs.length} document${docs.length > 1 ? 's' : ''}` } : null,
                        { label: 'Pack', value: selectedPack ? `${selectedPack.name}${selectedPack.price > 0 ? ` — ${selectedPack.price} MAD` : ' (Gratuit)'}` : 'Aucun' },
                      ] as Array<{ label: string; value: string } | null>)
                        .filter((r): r is { label: string; value: string } => r !== null)
                        .map((row) => (
                          <div key={row.label} className="flex items-start px-5 py-3 gap-4 bg-surface-light">
                            <span className="text-muted-foreground text-sm w-24 shrink-0 mt-0.5">{row.label}</span>
                            <span className="font-medium text-ink text-sm leading-relaxed">{row.value}</span>
                          </div>
                        ))}
                    </div>

                    {!user ? (
                      <div className="rounded-2xl border border-accent/40 bg-accent/10 p-5">
                        <div className="flex items-start gap-3">
                          <Lock className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-accent-700 mb-1">Connexion requise</p>
                            <p className="text-sm text-accent-700 mb-4">
                              Vous devez être connecté pour publier une annonce.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              <Link href={`/auth/login?redirect=${encodeURIComponent('/publier')}`}>
                                <Button size="sm" variant="navy">
                                  <User className="h-3.5 w-3.5 mr-1.5" />
                                  Se connecter
                                </Button>
                              </Link>
                              <Link href={`/auth/register?redirect=${encodeURIComponent('/publier')}`}>
                                <Button size="sm" variant="outline">Créer un compte</Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-success/30 bg-success/5 px-5 py-3.5 flex items-center gap-2.5 text-sm text-success">
                        <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                        Connecté en tant que{' '}
                        <strong>{user.firstName} {user.lastName}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Error banner ── */}
              {error && (
                <div className="mx-6 mb-4 rounded-xl bg-error/5 border border-error/30 px-4 py-3 flex items-center gap-2 text-sm text-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              {/* ── Navigation ── */}
              <div className="px-6 sm:px-8 pb-6 sm:pb-8 flex items-center justify-between gap-4 border-t border-border-subtle pt-5">
                {step > 1 ? (
                  <Button variant="ghost" onClick={back} disabled={isPending} className="gap-1.5 text-muted-foreground hover:text-ink">
                    <ChevronLeft className="h-4 w-4" />
                    Précédent
                  </Button>
                ) : (
                  <div />
                )}

                {step < STEPS.length ? (
                  <Button variant="navy" onClick={next} className="gap-1.5 px-7">
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    variant="navy"
                    onClick={submit}
                    disabled={isPending || !user}
                    className="gap-2 px-7 min-w-[170px]"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {uploadProgress || 'Publication…'}
                      </>
                    ) : (
                      <>
                        <BadgeCheck className="h-4 w-4" />
                        Publier l'annonce
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PublierPage() {
  return (
    <Suspense>
      <PublierPageContent />
    </Suspense>
  );
}

